import type { CivicInfo, Plot } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb } from "../geometry";

/**
 * World v4 (ADR-0015): Market Street runs east-west north of the Main Street shops, joined to Main Street by Willow
 * Avenue (west) and Aspen Avenue (east). The four attractions (ADR-0016) stand on its north side, facing south.
 */
export const MARKET_STREET_Z = 70;
export const MARKET_STREET_X_MIN = -240;
export const MARKET_STREET_X_MAX = 240;
export const WILLOW_AVENUE_X = -240;
export const ASPEN_AVENUE_X = 240;
const ROAD_HALF = 3;
const SIDEWALK = 4;
/** North edge of Market Street's north sidewalk: the attraction plots start here. */
export const MARKET_NORTH_FRONT_Z = MARKET_STREET_Z + ROAD_HALF + SIDEWALK; // 77

/** Exterior shell shared by the four attraction buildings (the game happens inside, in the venue's room). */
export const VENUE_BUILDING = {
  footprint: { width: 48, depth: 36 },
  heightMetres: 14,
  rooftopSign: { width: 26, height: 8 },
  storefrontSign: { width: 18, height: 3.6 },
  /** Plot frontage and depth. */
  plot: { width: 60, depth: 60 },
} as const;

const ENTRANCE_SETBACK = 2.9;
const DOOR_HALF_WIDTH = 2.4;

export interface VenueSite {
  id: string;
  cx: number;
  address: string;
  kind: Extract<CivicInfo["kind"], "casino" | "race_track" | "sports_center" | "truck_arena">;
  name: string;
  description: string;
  interior: "casino_floor" | "race_track" | "sports_center" | "truck_arena";
}

/** Addresses: Market Street's numbering by position (manifest.ts marketStreetNumber; odd on the north side). */
export const VENUE_SITES: readonly VenueSite[] = [
  {
    id: "v-04",
    cx: -165,
    address: "11 Market Street",
    kind: "casino",
    name: "Townsy Casino",
    description: "Blackjack, slots and the money wheel with 100 free play chips every visit (just for fun, no value), and a bar to meet people.",
    interior: "casino_floor",
  },
  {
    id: "v-05",
    cx: -55,
    address: "27 Market Street",
    kind: "race_track",
    name: "Townsy Downs",
    description: "Horse racing all day: pick the horse you think will win with 100 free play chips (just for fun), or watch from the stands.",
    interior: "race_track",
  },
  {
    id: "v-06",
    cx: 55,
    address: "43 Market Street",
    kind: "sports_center",
    name: "Townsy Sports Center",
    description: "Shoot hoops, take on the AI goalie on the soccer pitch, or play tennis against a friend or the computer.",
    interior: "sports_center",
  },
  {
    id: "v-07",
    cx: 165,
    address: "59 Market Street",
    kind: "truck_arena",
    name: "Monster Truck Arena",
    description: "Drive a monster truck over jumps and through the mud, set a lap time, and race whoever else is on the track.",
    interior: "truck_arena",
  },
];

/** The attraction plots (CIVIC, never purchasable): on Market Street's north side, fronts facing south. */
export function venuePlots(districtId: string): Plot[] {
  const B = VENUE_BUILDING;
  const frontZ = MARKET_NORTH_FRONT_Z;
  const wallZ = frontZ + 0.5;
  const roofY = B.heightMetres + B.rooftopSign.height / 2 + 0.3;
  return VENUE_SITES.map((v) => ({
    id: v.id,
    districtId,
    type: "CIVIC" as const,
    address: v.address,
    bounds: aabb(v.cx - B.plot.width / 2, frontZ, v.cx + B.plot.width / 2, frontZ + B.plot.depth),
    buildBounds: aabb(v.cx - B.footprint.width / 2, wallZ, v.cx + B.footprint.width / 2, wallZ + B.footprint.depth),
    entrance: { position: { x: v.cx, z: wallZ - ENTRANCE_SETBACK }, facing: YAW_NORTH },
    door: { position: { x: v.cx, z: wallZ }, direction: { x: 0, z: 1 }, halfWidth: DOOR_HALF_WIDTH },
    facing: YAW_SOUTH,
    civic: { kind: v.kind, name: v.name, description: v.description },
    interiorTemplate: v.interior,
    signAnchors: [
      { kind: "storefront" as const, position: { x: v.cx, y: 7.2, z: wallZ - 0.06 }, facing: YAW_SOUTH, width: B.storefrontSign.width, height: B.storefrontSign.height },
      { kind: "skyline" as const, position: { x: v.cx, y: roofY, z: wallZ + 1.2 }, facing: YAW_SOUTH, width: B.rooftopSign.width, height: B.rooftopSign.height },
    ],
    decorationAnchors: [],
    heightBand: 0,
  }));
}
